import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from './contexts/ThemeContext';
import { CartProvider } from './contexts/CartContext';
import './App.css';

import Navbar from './components/Navbar';
import Footer from './components/Footer';
import WhatsAppButton from './components/WhatsAppButton';

// Pages
import Home from './pages/Home';
import Pacotes from './pages/Pacotes';
import PackageDetail from './pages/PackageDetail';
import Sobre from './pages/Sobre';
import Contato from './pages/Contato';
import PoliticaCancelamento from './pages/PoliticaCancelamento';
import CheckoutStep1 from './pages/CheckoutStep1';
import CheckoutStep2 from './pages/CheckoutStep2';
import CheckoutStep3 from './pages/CheckoutStep3';
import CheckoutContract from './pages/CheckoutContract';
import CheckoutStep4 from './pages/CheckoutStep4';

// Admin Pages
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminPackages from './pages/admin/AdminPackages';
import AdminPackageForm from './pages/admin/AdminPackageForm';
import AdminSettings from './pages/admin/AdminSettings';
import AdminReservations from './pages/admin/AdminReservations';
import AdminImageLibrary from './pages/admin/AdminImageLibrary';
import AdminPageEditor from './pages/admin/AdminPageEditor';
import AdminFAQ from './pages/admin/AdminFAQ';
import AdminFinanceiro from './pages/admin/AdminFinanceiro';
import AdminContracts from './pages/admin/AdminContracts';
import AdminManualReservations from './pages/admin/AdminManualReservations';
import AdminOperationalResults from './pages/admin/AdminOperationalResults';
import AdminPassengers from './pages/admin/AdminPassengers';
import AdminDRE from './pages/admin/AdminDRE';
import AdminCashFlow from './pages/admin/AdminCashFlow';
import AdminCustomers from './pages/admin/AdminCustomers';
import AdminCommissions from './pages/admin/AdminCommissions';
import AdminCoupons from './pages/admin/AdminCoupons';
import AdminGeneralExpenses from './pages/admin/AdminGeneralExpenses';
import AdminEmails from './pages/admin/AdminEmails';
import AdminASAAS from './pages/admin/AdminASAAS';
import AdminLeadForms from './pages/admin/AdminLeadForms';
import AdminCustomPackages from './pages/admin/AdminCustomPackages';
import AdminCreditBags from './pages/admin/AdminCreditBags';
import AdminCancelRequests from './pages/admin/AdminCancelRequests';
import AdminMinorDocs from './pages/admin/AdminMinorDocs';
import AdminCommissionPayments from './pages/admin/AdminCommissionPayments';
import LeadForm from './pages/LeadForm';
import CustomPackages from './pages/CustomPackages';
import Cart from './pages/Cart';

// Customer Pages
import CustomerLogin from './pages/customer/CustomerLogin';
import CustomerDashboard from './pages/customer/CustomerDashboard';
import CustomerSettings from './pages/customer/CustomerSettings';
import ContractView from './pages/customer/ContractView';
import ForgotPassword from './pages/customer/ForgotPassword';
import ResetPassword from './pages/customer/ResetPassword';

// Componente para scroll to top em mudança de rota (incluindo voltar do navegador)
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // Desabilita restore automático do browser (que preserva scroll ao voltar)
    if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // dupla chamada garante em Safari e após render assíncrono
      window.scrollTo(0, 0);
      const t = setTimeout(() => window.scrollTo(0, 0), 30);
      return () => clearTimeout(t);
    }
  }, [pathname]);

  return null;
};

// Layout wrapper to conditionally show Navbar/Footer
const AppLayout = ({ children }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isLeadForm = location.pathname.startsWith('/f/');
  const hideChrome = isAdminRoute || isLeadForm;
  
  return (
    <>
      <ScrollToTop />
      {!hideChrome && <Navbar />}
      {children}
      {!hideChrome && <Footer />}
      {!hideChrome && <WhatsAppButton />}
    </>
  );
};

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <CartProvider>
          <div className="App">
            <BrowserRouter>
              <AppLayout>
                <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/pacotes" element={<Pacotes />} />
              <Route path="/pacotes-personalizados" element={<CustomPackages />} />
              <Route path="/pacote/:id" element={<PackageDetail />} />
              <Route path="/sobre" element={<Sobre />} />
              <Route path="/contato" element={<Contato />} />
              <Route path="/politica-de-cancelamento" element={<PoliticaCancelamento />} />
              
              {/* Checkout Routes */}
              <Route path="/checkout/:packageId/:dateId" element={<CheckoutStep1 />} />
              <Route path="/checkout/dados-pessoais" element={<CheckoutStep2 />} />
              <Route path="/checkout/embarque" element={<CheckoutStep3 />} />
              <Route path="/checkout/contrato" element={<CheckoutContract />} />
              <Route path="/checkout/pagamento" element={<CheckoutStep4 />} />
              
              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/pacotes" element={<AdminPackages />} />
              <Route path="/admin/pacotes/novo" element={<AdminPackageForm />} />
              <Route path="/admin/pacotes/editar/:id" element={<AdminPackageForm />} />
              <Route path="/admin/configuracoes" element={<AdminSettings />} />
              <Route path="/admin/reservas" element={<AdminReservations />} />
              <Route path="/admin/imagens" element={<AdminImageLibrary />} />
              <Route path="/admin/pagina/:page" element={<AdminPageEditor />} />
              <Route path="/admin/faq" element={<AdminFAQ />} />
              <Route path="/admin/financeiro" element={<AdminFinanceiro />} />
              <Route path="/admin/contratos" element={<AdminContracts />} />
              <Route path="/admin/emails" element={<AdminEmails />} />
              <Route path="/admin/reservas-manuais" element={<AdminManualReservations />} />
              <Route path="/admin/resultados-operacionais" element={<AdminOperationalResults />} />
              <Route path="/admin/passageiros" element={<AdminPassengers />} />
              <Route path="/admin/dre" element={<AdminDRE />} />
              <Route path="/admin/fluxo-caixa" element={<AdminCashFlow />} />
              <Route path="/admin/clientes" element={<AdminCustomers />} />
              <Route path="/admin/comissoes" element={<AdminCommissions />} />
              <Route path="/admin/cupons" element={<AdminCoupons />} />
              <Route path="/admin/bolsas-credito" element={<AdminCreditBags />} />
              <Route path="/admin/solicitacoes-cancelamento" element={<AdminCancelRequests />} />
              <Route path="/admin/documentos-menores" element={<AdminMinorDocs />} />
              <Route path="/admin/pagamentos-comissao" element={<AdminCommissionPayments />} />
              <Route path="/admin/despesas-gerais" element={<AdminGeneralExpenses />} />
              <Route path="/admin/asaas" element={<AdminASAAS />} />
              <Route path="/admin/lead-forms" element={<AdminLeadForms />} />
              <Route path="/admin/pacotes-personalizados" element={<AdminCustomPackages />} />
              <Route path="/admin" element={<AdminDashboard />} />
              
              {/* Customer Routes */}
              <Route path="/cliente/login" element={<CustomerLogin />} />
              <Route path="/cliente/esqueci-senha" element={<ForgotPassword />} />
              <Route path="/cliente/redefinir-senha" element={<ResetPassword />} />
              <Route path="/minha-conta" element={<CustomerDashboard />} />
              <Route path="/cliente/configuracoes" element={<CustomerSettings />} />
              <Route path="/contrato/:reservationId" element={<ContractView />} />
              
              {/* Lead Form - Público (sem navbar/footer) */}
              <Route path="/f/:slug" element={<LeadForm />} />

              {/* Carrinho */}
              <Route path="/carrinho" element={<Cart />} />
            </Routes>
          </AppLayout>
        </BrowserRouter>
      </div>
      </CartProvider>
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
